import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Copy, Share2 } from 'lucide-react';

const CATEGORY_LABELS = {
  en: { products: 'Product Launch', events: 'Events', awards: 'Awards', industry: 'Industry', press: 'Press Release' },
  ar: { products: 'إطلاق منتج', events: 'الفعاليات', awards: 'الجوائز', industry: 'الصناعة', press: 'بيان صحفي' },
};

const NewsArticleModal = ({ data }) => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const [article, setArticle] = useState(data?.article || null);
  const [loading, setLoading] = useState(!data?.article);
  const [modalLang, setModalLang] = useState(language);
  const [readProgress, setReadProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (data?.slug && !data?.article) {
      fetch(`/api/news/${data.slug}`)
        .then(r => r.json())
        .then(setArticle)
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [data]);

  useEffect(() => {
    setModalLang(language);
  }, [language, data?.slug, data?.article?.slug]);

  // Reading progress bar
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const progress = scrollHeight <= clientHeight ? 100 : (scrollTop / (scrollHeight - clientHeight)) * 100;
      setReadProgress(Math.min(100, progress));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [article]);

  const handleWhatsApp = () => {
    const url = `${window.location.origin}/?article=${article?.slug}`;
    const sharedTitle = modalLang === 'ar' ? article?.title_ar : article?.title_en;
    const text = encodeURIComponent(`${sharedTitle}\n${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/?article=${article?.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="flex items-center justify-center h-40 text-white/40 text-sm">
        {language === 'ar' ? 'المقال غير موجود.' : 'Article not found.'}
      </div>
    );
  }

  const lang = modalLang;
  const isRtl = lang === 'ar';
  const title = lang === 'ar' ? article.title_ar : article.title_en;
  const body = lang === 'ar' ? article.body_ar : article.body_en;
  const categoryLabel = CATEGORY_LABELS[lang]?.[article.category] || article.category;
  const publishedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-AE', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : '';

  return (
    <div
      ref={scrollRef}
      className="relative overflow-y-auto max-h-[85vh]"
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
    >
      {/* Reading progress bar */}
      <div className="sticky top-0 left-0 right-0 z-10 h-0.5 bg-transparent">
        <div
          className="h-full bg-[#D4AF37] transition-all duration-100"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      {/* Cover Image */}
      {article.cover_image_url ? (
        <div className="relative h-52 sm:h-64 overflow-hidden">
          <img
            src={article.cover_image_url}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      ) : (
        <div className={`h-2 ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`} />
      )}

      {/* Article content */}
      <div className="p-6 sm:p-8">
        {/* Category & date */}
        <div className={`flex items-center gap-3 mb-4 ${isRtl ? 'flex-row-reverse justify-end' : ''}`}>
          <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest bg-[#D4AF37] text-black rounded-[2px]">
            {categoryLabel}
          </span>
          {publishedDate && (
            <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              {publishedDate}
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className={`font-display text-2xl sm:text-3xl font-bold leading-snug mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {title}
        </h2>

        <div className={`w-12 h-0.5 bg-[#D4AF37] mb-6 ${isRtl ? 'ml-auto mr-0' : ''}`} />

        {/* Body */}
        <div className={`text-sm sm:text-base leading-relaxed space-y-4 ${isDark ? 'text-white/75' : 'text-gray-700'}`}>
          {body?.split('\n\n').map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>

        {/* Footer actions */}
        <div className={`mt-8 pt-6 border-t flex flex-wrap items-center justify-between gap-4 ${isRtl ? 'flex-row-reverse' : ''} ${
          isDark ? 'border-[rgba(212,175,55,0.15)]' : 'border-gray-200'
        }`}>
          {/* Language toggle */}
          <div className="flex items-center gap-1 rounded-sm overflow-hidden border border-[rgba(212,175,55,0.3)]">
            {['en', 'ar'].map(l => (
              <button
                key={l}
                onClick={() => setModalLang(l)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase transition-colors ${
                  modalLang === l
                    ? 'bg-[#D4AF37] text-black'
                    : isDark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {l === 'en' ? 'EN' : 'AR'}
              </button>
            ))}
          </div>

          {/* Share buttons */}
          <div className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={handleWhatsApp}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-sm bg-[#25D366] text-white hover:bg-[#1ebe5c] transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
            >
              <Share2 className="w-3.5 h-3.5" />
              {lang === 'ar' ? 'واتساب' : 'WhatsApp'}
            </button>
            <button
              onClick={handleCopyLink}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${isRtl ? 'flex-row-reverse' : ''} ${
                copied
                  ? 'border-[#D4AF37] text-[#D4AF37]'
                  : isDark
                    ? 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-800'
              }`}
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? (lang === 'ar' ? 'تم النسخ!' : 'Copied!') : (lang === 'ar' ? 'نسخ الرابط' : 'Copy Link')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsArticleModal;
